/**
 * Utilidad para acceso a la versión de la aplicación
 * @module version
 */

/**
 * Versión actual de la aplicación desde package.json
 * Inyectada automáticamente en build time por Vite
 * @type {string}
 * @constant
 * @example
 * ```js
 * import { APP_VERSION } from '@/utils/version'
 * console.log(`v${APP_VERSION}`) // "v8.0.0"
 * ```
 */
// eslint-disable-next-line no-undef
export const APP_VERSION = __APP_VERSION__

/**
 * Retorna la versión formateada con prefijo "v"
 * @returns {string} Versión formateada (ej: "v8.0.0")
 * @example
 * ```js
 * import { getFormattedVersion } from '@/utils/version'
 * console.log(getFormattedVersion()) // "v8.0.0"
 * ```
 */
export function getFormattedVersion() {
  return `v${APP_VERSION}`
}

/**
 * Retorna información completa de la versión
 * @returns {Object} Objeto con información de versión
 * @property {string} version - Versión completa (ej: "8.0.0")
 * @property {string} formatted - Versión formateada (ej: "v8.0.0")
 * @property {number} major - Versión major (ej: 8)
 * @property {number} minor - Versión minor (ej: 0)
 * @property {number} patch - Versión patch (ej: 0)
 * @property {string|null} prerelease - Prerelease tag (ej: "alpha.1" o null)
 * @example
 * ```js
 * import { getVersionInfo } from '@/utils/version'
 * const info = getVersionInfo()
 * console.log(info)
 * // {
 * //   version: "8.0.0",
 * //   formatted: "v8.0.0",
 * //   major: 8,
 * //   minor: 0,
 * //   patch: 0,
 * //   prerelease: null
 * // }
 * ```
 */
export function getVersionInfo() {
  // Parsear versión semántica (ej: "8.0.0" o "8.0.0-alpha.1")
  const match = APP_VERSION.match(/^(\d+)\.(\d+)\.(\d+)(?:-(.+))?$/)

  if (!match) {
    // Si no puede parsear, retornar valores por defecto
    return {
      version: APP_VERSION,
      formatted: `v${APP_VERSION}`,
      major: 0,
      minor: 0,
      patch: 0,
      prerelease: null,
    }
  }

  const [, major, minor, patch, prerelease] = match

  return {
    version: APP_VERSION,
    formatted: `v${APP_VERSION}`,
    major: parseInt(major, 10),
    minor: parseInt(minor, 10),
    patch: parseInt(patch, 10),
    prerelease: prerelease || null,
  }
}

/**
 * Compara dos versiones semánticas
 * @param {string} v1 - Primera versión (ej: "8.0.0")
 * @param {string} v2 - Segunda versión (ej: "7.5.2")
 * @returns {number} -1 si v1 < v2, 0 si v1 == v2, 1 si v1 > v2
 * @example
 * ```js
 * import { compareVersions } from '@/utils/version'
 * compareVersions("8.0.0", "7.5.2") // 1
 * compareVersions("7.5.2", "8.0.0") // -1
 * compareVersions("8.0.0", "8.0.0") // 0
 * ```
 */
export function compareVersions(v1, v2) {
  const parseVersion = v => {
    const match = v.match(/^(\d+)\.(\d+)\.(\d+)/)
    if (!match) return [0, 0, 0]
    return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)]
  }

  const [major1, minor1, patch1] = parseVersion(v1)
  const [major2, minor2, patch2] = parseVersion(v2)

  if (major1 !== major2) return major1 > major2 ? 1 : -1
  if (minor1 !== minor2) return minor1 > minor2 ? 1 : -1
  if (patch1 !== patch2) return patch1 > patch2 ? 1 : -1

  return 0
}

/**
 * Verifica si la versión actual es mayor o igual a una versión específica
 * @param {string} targetVersion - Versión a comparar (ej: "8.0.0")
 * @returns {boolean} true si la versión actual >= targetVersion
 * @example
 * ```js
 * import { isVersionAtLeast } from '@/utils/version'
 * isVersionAtLeast("7.0.0") // true (si APP_VERSION es "8.0.0")
 * isVersionAtLeast("9.0.0") // false (si APP_VERSION es "8.0.0")
 * ```
 */
export function isVersionAtLeast(targetVersion) {
  return compareVersions(APP_VERSION, targetVersion) >= 0
}

/**
 * Retorna el nombre del cache de Sentry con versión
 * Útil para invalidación de cache en actualizaciones
 * @returns {string} Nombre del cache versionado
 * @example
 * ```js
 * import { getSentryCacheName } from '@/utils/version'
 * console.log(getSentryCacheName()) // "sentry-offline-events@8.0.0"
 * ```
 */
export function getSentryCacheName() {
  return `sentry-offline-events@${APP_VERSION}`
}

/**
 * Retorna el nombre del store de Sentry con versión
 * @returns {string} Nombre del store versionado
 * @example
 * ```js
 * import { getSentryStoreName } from '@/utils/version'
 * console.log(getSentryStoreName()) // "events-queue@8.0.0"
 * ```
 */
export function getSentryStoreName() {
  return `events-queue@${APP_VERSION}`
}

export default {
  APP_VERSION,
  getFormattedVersion,
  getVersionInfo,
  compareVersions,
  isVersionAtLeast,
  getSentryCacheName,
  getSentryStoreName,
}
